import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, totalPrice } = useCart();

  // Redirect to cart if empty (must not be conditional around hooks)
  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart');
    }
  }, [items.length, navigate]);

  // Form state
  const [form, setForm] = useState({
    name: '',
    phone: '',
    region: 'الداخل',
    municipality: '',
    city: '',
    address: '',
    email: '',
    altPhone: '',
    notes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = 'الاسم مطلوب';
    if (!/^\+?\d{7,15}$/.test(form.phone.trim())) newErrors.phone = 'رقم الهاتف غير صالح';
    if (!form.region) newErrors.region = 'المنطقة مطلوبة';
    if (!form.municipality.trim()) newErrors.municipality = 'المحافظة مطلوبة';
    if (!form.city.trim()) newErrors.city = 'المدينة مطلوبة';
    if (!form.address.trim()) newErrors.address = 'العنوان مطلوب';
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) newErrors.email = 'البريد الإلكتروني غير صالح';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [submitting, setSubmitting] = useState(false);

  // Shipping calculation based on selected region
  const shippingCost = useMemo(() => {
    switch (form.region) {
      case 'الضفة الغربية':
        return 20;
      case 'القدس':
        return 35;
      case 'الداخل':
        return 70;
      default:
        return 0;
    }
  }, [form.region]);

  const grandTotal = useMemo(() => Number((totalPrice + shippingCost).toFixed(2)), [totalPrice, shippingCost]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    if (items.length === 0) return;

    try {
      setSubmitting(true);

      // Map cart items to order items shape expected by backend
      const orderItems = items.map((it) => ({
        productId: it.id,
        name: it.name,
        price: it.price,
        quantity: it.quantity,
        selectedColor: it.selectedColor
      }));

      // Split name to first/last where possible
      const nameParts = form.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const payload = {
        items: orderItems,
        customerInfo: {
          firstName,
          lastName,
          email: form.email.trim() || undefined,
          phone: form.phone.trim(),
          altPhone: form.altPhone.trim() || undefined,
          address: form.address.trim(),
          city: form.city.trim(),
          region: form.region,
          municipality: form.municipality.trim()
        },
        totalAmount: grandTotal,
        status: 'pending' as const,
        notes: form.notes?.trim() || undefined
      };

      // Do not create order yet. Persist payload and create it only after
      // user confirms payment method in /checkout/payment.
      sessionStorage.setItem('pendingOrderPayload', JSON.stringify(payload));
      sessionStorage.setItem('pendingOrderTotal', grandTotal.toFixed(2));
      sessionStorage.removeItem('lastOrderId');
      sessionStorage.removeItem('lastOrderNumber');
      sessionStorage.removeItem('lastOrderTotal');
      navigate('/checkout/payment');
    } catch (error: any) {
      console.error('Failed to create order', error);
      const message = error?.response?.data?.error || error?.message || 'تعذر إرسال الطلب. الرجاء المحاولة مرة أخرى.';
      alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg py-8 transition-colors duration-300">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/cart')}
            className="flex items-center text-gray-600 dark:text-dark-textSecondary hover:text-pink-600 dark:hover:text-amber-400 transition-colors duration-300 mb-4"
          >
            <ArrowLeft className="h-5 w-5 ml-2" />
            العودة إلى السلة
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text transition-colors duration-300">إتمام الطلب</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <form className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6" onSubmit={handleSubmit} dir="rtl">
            {/* Payment method selection moved to the next page after confirming */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">الاسم<span className="text-red-500"> *</span></label>
                <input type="text" value={form.name} onChange={(e) => update('name', e.target.value)} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500" placeholder="الاسم" />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">رقم الهاتف<span className="text-red-500"> *</span></label>
                <input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500" placeholder="رقم الهاتف" />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
            </div>

            {/* Delivery Region */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">التسليم إلى:<span className="text-red-500"> *</span></label>
              <select value={form.region} onChange={(e) => update('region', e.target.value)} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500">
                <option value="الداخل">الداخل</option>
                <option value="الضفة الغربية">الضفة الغربية</option>
                <option value="القدس">القدس</option>
              </select>
              {errors.region && <p className="text-red-500 text-xs mt-1">{errors.region}</p>}
            </div>

            {/* Address details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">المحافظة<span className="text-red-500"> *</span></label>
                <input type="text" value={form.municipality} onChange={(e) => update('municipality', e.target.value)} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500" placeholder="اكتب اسم المحافظة" />
                {errors.municipality && <p className="text-red-500 text-xs mt-1">{errors.municipality}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">المدينة<span className="text-red-500"> *</span></label>
                <input type="text" value={form.city} onChange={(e) => update('city', e.target.value)} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500" placeholder="اكتب اسم المدينة" />
                {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">العنوان التفصيلي<span className="text-red-500"> *</span></label>
              <input type="text" value={form.address} onChange={(e) => update('address', e.target.value)} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500" placeholder="اسم الشارع، رقم المنزل، معالم قريبة..." />
              {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
            </div>

            {/* Email and alt phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">البريد الإلكتروني (اختياري)</label>
                <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500" placeholder="example@email.com" />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">رقم هاتف آخر (اختياري)</label>
                <input type="tel" value={form.altPhone} onChange={(e) => update('altPhone', e.target.value)} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500" placeholder="إن وُجد" />
              </div>
            </div>

            {/* Notes */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ملاحظات إضافية (اختياري)</label>
              <textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} rows={4} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500" placeholder="أي تفاصيل إضافية للتوصيل أو الطلب"></textarea>
            </div>

            <div className="mt-6">
              <button type="submit" disabled={submitting} className="w-full btn-primary py-3 text-lg font-semibold disabled:opacity-60">
                {submitting ? 'جارٍ الإرسال...' : 'تأكيد وإرسال الطلب'}
              </button>
            </div>
          </form>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">ملخص الطلب</h2>
              <div className="space-y-3 mb-6">
                {items.map((item) => (
                  <div key={item.id} className="flex justify-between items-center">
                    <span className="text-gray-700 dark:text-gray-300">{item.name} × {item.quantity}</span>
                    <span className="text-gray-900 dark:text-dark-text">₪{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-700 dark:text-gray-300">
                    <span>الإجمالي بدون الشحن:</span>
                    <span>₪{totalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-700 dark:text-gray-300">
                    <span>تكلفة الشحن ({form.region}):</span>
                    <span>₪{shippingCost.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                    <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-dark-text">
                      <span>الإجمالي مع الشحن:</span>
                      <span>₪{grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <button onClick={() => navigate('/cart')} className="w-full btn-secondary py-2">العودة للسلة</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
